import { useContext, useMemo } from 'react';
import { SHARE_SETTINGS_MODE } from '../../constants';
import type { ShareSettingsContextValue, ShareSettingsScopeId } from '../../types';
import { ShareSettingsContext } from '../components/ShareSettingsContext';
import { isEqualShareSettingsViewModel } from '../utils';

export function useShareSettings(): ShareSettingsContextValue {
    const providerContext = useContext(ShareSettingsContext);

    if (!providerContext) {
        throw new Error('useShareSettings must be used within a ShareSettingsProvider');
    }

    const shareSettings = useMemo(() => {
        const { scopes, saveShareSettings, commitCreatedShareSettings, resetShareSettings, isSavingShareSettings } =
            providerContext;

        const getShareSettings: ShareSettingsContextValue['getShareSettings'] = (scopeId: ShareSettingsScopeId) => {
            const scopeState = scopes[scopeId];

            if (!scopeState) {
                return undefined;
            }

            return scopeState.currentShareSettings;
        };

        const checkHasUnsavedShareSettings: ShareSettingsContextValue['checkHasUnsavedShareSettings'] = (
            scopeId: ShareSettingsScopeId
        ) => {
            const scopeState = scopes[scopeId];

            if (!scopeState || scopeState.mode === SHARE_SETTINGS_MODE.create) {
                return false;
            }

            return !isEqualShareSettingsViewModel(scopeState.initialShareSettings, scopeState.currentShareSettings);
        };

        const checkHasUncommittedShareSettings: ShareSettingsContextValue['checkHasUncommittedShareSettings'] = (
            scopeId: ShareSettingsScopeId
        ) => {
            const scopeState = scopes[scopeId];

            if (!scopeState || scopeState.mode === SHARE_SETTINGS_MODE.update) {
                return false;
            }

            return !isEqualShareSettingsViewModel(scopeState.committedShareSettings, scopeState.currentShareSettings);
        };

        return {
            getShareSettings,
            checkHasUnsavedShareSettings,
            checkHasUncommittedShareSettings,
            saveShareSettings,
            commitCreatedShareSettings,
            resetShareSettings,
            isSavingShareSettings
        };
    }, [providerContext]);

    return shareSettings;
}
